import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { useMnaRadar } from '../lib/api';
import type { DistressStage, RadarPointDto, SizeBand } from '../types';
import {
  DISTRESS_COLORS,
  DISTRESS_LABELS,
  SIZE_BAND_SHORT,
  SUB_SEGMENT_SHORT,
  DeltaBadge,
  ScorePair,
} from './_shared';

// Tab 2 — Radar (the flagship). Fit × availability scatter, plain SVG (no charting lib —
// WWD renders its charts with primitives too). x = availability 0–100, y = fit 0–100,
// quadrant guides at 50/50; point size = sizeBand, color = distressStage; a movement
// trail (previous 30d position → today, arrowhead at today) renders when the score
// history yields non-null deltas. Click a point → the company dossier.
//
// Availability 0 is the MODE (healthy companies with no events), so plotting zeros on the
// axis smeared half the universe into one unreadable column. Zeros now get their own
// labeled "no signal" gutter lane left of the plot, beeswarm-packed so points stay
// individually hoverable — zero is the absence of signal, not a small number, and jittering
// it into the plot would fake availability that isn't there.

const W = 760;
const H = 560;
const GUTTER_W = 72;
const GUTTER_GAP = 14;
const M = { top: 20, right: 20, bottom: 52, left: 56 + GUTTER_W + GUTTER_GAP };
const GUTTER_X = 56;
const PW = W - M.left - M.right;
const PH = H - M.top - M.bottom;

function px(availability: number): number {
  return M.left + (availability / 100) * PW;
}
function py(fit: number): number {
  return M.top + (1 - fit / 100) * PH;
}

/**
 * Beeswarm packing for the zero-availability gutter: y is pinned to fit; x spreads across
 * the lane (then tiny y nudges as a last resort) until the circle stops colliding with
 * already-placed neighbours. Deterministic — sorted by fit, then slug.
 */
function packGutter(points: RadarPointDto[]): Map<string, { x: number; y: number }> {
  const cx = GUTTER_X + GUTTER_W / 2;
  const placed: { x: number; y: number; r: number }[] = [];
  const pos = new Map<string, { x: number; y: number }>();
  const sorted = [...points].sort((a, b) => b.fit - a.fit || a.slug.localeCompare(b.slug));
  for (const p of sorted) {
    const r = SIZE_RADIUS[p.sizeBand];
    const maxOff = GUTTER_W / 2 - r - 3;
    const yBase = py(p.fit);
    let best: { x: number; y: number } | null = null;
    for (const dy of [0, 3, -3, 6, -6]) {
      for (let step = 0; step * 6 <= maxOff; step++) {
        for (const sign of step === 0 ? [1] : [1, -1]) {
          const x = cx + sign * step * 6;
          const y = yBase + dy;
          if (!placed.some((q) => (q.x - x) ** 2 + (q.y - y) ** 2 < (q.r + r + 1.5) ** 2)) {
            best = { x, y };
            break;
          }
        }
        if (best) break;
      }
      if (best) break;
    }
    const final = best ?? { x: cx, y: yBase }; // dense same-fit cluster: accept slight overlap
    placed.push({ ...final, r });
    pos.set(p.slug, final);
  }
  return pos;
}

const SIZE_RADIUS: Record<SizeBand, number> = {
  micro: 4.5,
  small: 6,
  mid: 8,
  large: 10.5,
  unknown: 5,
};

const QUADRANTS = [
  { label: 'Act now', hint: 'high fit · high availability', x: px(75), y: py(97) },
  { label: 'Cultivate', hint: 'high fit · low availability', x: px(25), y: py(97) },
  { label: 'Opportunistic', hint: 'low fit · high availability', x: px(75), y: py(6) },
  { label: 'Monitor', hint: 'low fit · low availability', x: px(25), y: py(6) },
];

const TICKS = [0, 25, 50, 75, 100];

const DISTRESS_LEGEND: DistressStage[] = ['none', 'early_signal', 'pre_distress', 'in_process', 'resolved'];
const SIZE_LEGEND: SizeBand[] = ['micro', 'small', 'mid', 'large'];

export default function Radar() {
  const { data: points, isLoading, error } = useMnaRadar();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState<string | null>(null);
  // Trails default to hover-only: with the since-first-score delta fallback most points
  // carry movement, and rendering every trail turned the plot into dashed-line noise
  // (2026-07-06 feedback). The toggle restores the full-motion view on demand.
  const [showTrails, setShowTrails] = useState(false);

  // Hovered point last so it paints on top of its neighbours.
  const ordered = useMemo(() => {
    const pts = points ?? [];
    if (!hovered) return pts;
    return [...pts.filter((p) => p.slug !== hovered), ...pts.filter((p) => p.slug === hovered)];
  }, [points, hovered]);

  // availability 0 → beeswarm-packed gutter lane; everything else → the plot.
  const positions = useMemo(() => {
    const pts = points ?? [];
    const pos = packGutter(pts.filter((p) => p.availability <= 0));
    for (const p of pts) {
      if (p.availability > 0) pos.set(p.slug, { x: px(p.availability), y: py(p.fit) });
    }
    return pos;
  }, [points]);

  if (isLoading) return <LoadingSkeleton lines={10} />;
  if (error || !points) return <ErrorState message={(error as Error | null)?.message} />;

  const hoveredPoint = points.find((p) => p.slug === hovered) ?? null;
  const gutterCount = points.filter((p) => p.availability <= 0).length;

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Radar</CardTitle>
        <CardDescription className="mt-2">
          Every scored company plotted by strategic fit (vertical) against acquisition
          availability (horizontal). Companies with no availability signal yet (score 0) sit
          in the dashed lane on the left rather than smearing the axis. Trails show 30-day
          movement — availability is the axis events push. Click a point for the dossier.
        </CardDescription>
      </Card>

      {points.length === 0 ? (
        <Card>
          <CardDescription>No scored companies yet — the Radar fills as fit-score runs land.</CardDescription>
        </Card>
      ) : (
        <Card>
          <div className="relative">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className="w-full"
              role="img"
              aria-label="Fit versus availability scatter of scored companies"
            >
              {/* zero-availability gutter lane */}
              {gutterCount > 0 && (
                <g aria-hidden>
                  <rect
                    x={GUTTER_X}
                    y={M.top}
                    width={GUTTER_W}
                    height={PH}
                    fill="var(--surface-elevated)"
                    fillOpacity={0.55}
                    stroke="var(--border)"
                    strokeDasharray="4 3"
                    rx={4}
                  />
                  <text
                    x={GUTTER_X + GUTTER_W / 2}
                    y={M.top + PH + 18}
                    textAnchor="middle"
                    fontSize={10}
                    fill="var(--text-muted)"
                  >
                    avail 0
                  </text>
                  <text
                    x={GUTTER_X + GUTTER_W / 2}
                    y={M.top + PH + 31}
                    textAnchor="middle"
                    fontSize={9}
                    fill="var(--text-subtle)"
                  >
                    no signal ({gutterCount})
                  </text>
                </g>
              )}

              {/* plot frame + gridlines */}
              <rect x={M.left} y={M.top} width={PW} height={PH} fill="none" stroke="var(--border)" />
              {TICKS.map((t) => (
                <g key={t}>
                  <line x1={px(t)} y1={M.top} x2={px(t)} y2={M.top + PH} stroke="var(--border)" strokeOpacity={0.45} />
                  <line x1={M.left} y1={py(t)} x2={M.left + PW} y2={py(t)} stroke="var(--border)" strokeOpacity={0.45} />
                  <text x={px(t)} y={M.top + PH + 18} textAnchor="middle" fontSize={11} fill="var(--text-muted)">
                    {t}
                  </text>
                  <text x={M.left - 10} y={py(t) + 4} textAnchor="end" fontSize={11} fill="var(--text-muted)">
                    {t}
                  </text>
                </g>
              ))}

              {/* quadrant guides at 50/50 */}
              <line x1={px(50)} y1={M.top} x2={px(50)} y2={M.top + PH} stroke="var(--border-strong)" strokeDasharray="5 4" />
              <line x1={M.left} y1={py(50)} x2={M.left + PW} y2={py(50)} stroke="var(--border-strong)" strokeDasharray="5 4" />
              {QUADRANTS.map((q) => (
                <g key={q.label}>
                  <text x={q.x} y={q.y} textAnchor="middle" fontSize={13} fontWeight={700} fill="var(--text-muted)" opacity={0.9}>
                    {q.label.toUpperCase()}
                  </text>
                  <text x={q.x} y={q.y + 14} textAnchor="middle" fontSize={10} fill="var(--text-subtle)">
                    {q.hint}
                  </text>
                </g>
              ))}

              {/* axis titles */}
              <text x={M.left + PW / 2} y={H - 10} textAnchor="middle" fontSize={12} fill="var(--text-muted)" style={{ letterSpacing: '0.12em' }}>
                AVAILABILITY →
              </text>
              <text
                x={16}
                y={M.top + PH / 2}
                textAnchor="middle"
                fontSize={12}
                fill="var(--text-muted)"
                style={{ letterSpacing: '0.12em' }}
                transform={`rotate(-90 16 ${M.top + PH / 2})`}
              >
                FIT →
              </text>

              {/* movement trails under the points (plot only — a trail into the gutter
                  would draw availability that no longer exists). Hover-only by default. */}
              {ordered
                .filter((p) => p.availability > 0 && (showTrails || hovered === p.slug))
                .map((p) => (
                  <Trail key={`trail-${p.slug}`} point={p} />
                ))}

              {/* points */}
              {ordered.map((p) => {
                const isHovered = hovered === p.slug;
                const pos = positions.get(p.slug) ?? { x: px(p.availability), y: py(p.fit) };
                return (
                  <circle
                    key={p.slug}
                    cx={pos.x}
                    cy={pos.y}
                    r={SIZE_RADIUS[p.sizeBand] * (isHovered ? 1.25 : 1)}
                    fill={DISTRESS_COLORS[p.distressStage]}
                    fillOpacity={isHovered ? 1 : 0.85}
                    stroke="var(--surface)"
                    strokeWidth={1.5}
                    className="cursor-pointer"
                    tabIndex={0}
                    role="link"
                    aria-label={`${p.name}: fit ${p.fit}, availability ${p.availability}. Open dossier.`}
                    onMouseEnter={() => setHovered(p.slug)}
                    onMouseLeave={() => setHovered((h) => (h === p.slug ? null : h))}
                    onFocus={() => setHovered(p.slug)}
                    onBlur={() => setHovered((h) => (h === p.slug ? null : h))}
                    onClick={() => navigate(`/mna/companies/${p.slug}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') navigate(`/mna/companies/${p.slug}`);
                    }}
                  >
                    <title>{`${p.name} — fit ${p.fit} / availability ${p.availability}`}</title>
                  </circle>
                );
              })}
            </svg>

            {/* hover tooltip (HTML, positioned over the SVG) */}
            {hoveredPoint && (
              <Tooltip
                point={hoveredPoint}
                pos={positions.get(hoveredPoint.slug) ?? { x: px(hoveredPoint.availability), y: py(hoveredPoint.fit) }}
              />
            )}
          </div>

          {/* legend */}
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-border pt-3">
            <div className="flex flex-wrap items-center gap-3">
              {DISTRESS_LEGEND.map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5 text-xs text-muted">
                  <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: DISTRESS_COLORS[s] }} />
                  {DISTRESS_LABELS[s]}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {SIZE_LEGEND.map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 text-xs text-muted">
                  <svg width={SIZE_RADIUS[b] * 2 + 2} height={SIZE_RADIUS[b] * 2 + 2} aria-hidden>
                    <circle
                      cx={SIZE_RADIUS[b] + 1}
                      cy={SIZE_RADIUS[b] + 1}
                      r={SIZE_RADIUS[b]}
                      fill="none"
                      stroke="var(--text-muted)"
                      strokeWidth={1.2}
                    />
                  </svg>
                  {SIZE_BAND_SHORT[b]}
                </span>
              ))}
            </div>
            <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 text-xs text-muted">
              <input
                type="checkbox"
                checked={showTrails}
                onChange={(e) => setShowTrails(e.target.checked)}
                className="accent-(--accent)"
              />
              Show all trails
            </label>
            <span className="w-full text-xs text-subtle">
              trail = movement since previous score (hover a point for its trail) · dashed lane = availability 0 (no signal yet)
            </span>
          </div>
        </Card>
      )}
    </div>
  );
}

/** 30-day movement trail: previous position → today, arrowhead at today. */
function Trail({ point }: { point: RadarPointDto }) {
  const { fitDelta30d: df, availabilityDelta30d: da } = point;
  if (df === null || da === null || (df === 0 && da === 0)) return null;
  const x2 = px(point.availability);
  const y2 = py(point.fit);
  const x1 = px(Math.max(0, Math.min(100, point.availability - da)));
  const y1 = py(Math.max(0, Math.min(100, point.fit - df)));
  // Arrowhead: two short strokes at the tip, angled off the trail direction.
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const ah = 7;
  const a1 = angle + Math.PI - 0.45;
  const a2 = angle + Math.PI + 0.45;
  const color = DISTRESS_COLORS[point.distressStage];
  return (
    <g aria-hidden opacity={0.65}>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={1.5} strokeDasharray="3 3" />
      <line x1={x2} y1={y2} x2={x2 + ah * Math.cos(a1)} y2={y2 + ah * Math.sin(a1)} stroke={color} strokeWidth={1.5} />
      <line x1={x2} y1={y2} x2={x2 + ah * Math.cos(a2)} y2={y2 + ah * Math.sin(a2)} stroke={color} strokeWidth={1.5} />
    </g>
  );
}

function Tooltip({ point, pos }: { point: RadarPointDto; pos: { x: number; y: number } }) {
  // % positioning keeps the tooltip glued to the point across responsive widths.
  const leftPct = (pos.x / W) * 100;
  const topPct = (pos.y / H) * 100;
  const flip = leftPct > 62;
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute z-10 w-60 rounded-(--radius-md) border border-border bg-surface-elevated p-3 shadow-[var(--shadow-md)]"
      style={{
        left: `${leftPct}%`,
        top: `${topPct}%`,
        transform: `translate(${flip ? 'calc(-100% - 14px)' : '14px'}, -50%)`,
      }}
    >
      <p className="text-sm font-semibold text-text">{point.name}</p>
      <p className="mt-0.5 text-xs text-muted">
        {point.subSegments.map((s) => SUB_SEGMENT_SHORT[s]).join(' · ')} · {SIZE_BAND_SHORT[point.sizeBand]}
      </p>
      <div className="mt-2">
        <ScorePair fit={point.fit} availability={point.availability} />
      </div>
      <div className="mt-1.5 flex items-center gap-3">
        <DeltaBadge delta={point.fitDelta30d} label="Fit" />
        <DeltaBadge delta={point.availabilityDelta30d} label="Availability" />
        {point.fitDelta30d === null && point.availabilityDelta30d === null && (
          <span className="text-xs italic text-subtle">first score this cycle</span>
        )}
      </div>
      <p className="mt-1.5 text-xs" style={{ color: DISTRESS_COLORS[point.distressStage] }}>
        {DISTRESS_LABELS[point.distressStage]}
      </p>
    </div>
  );
}
